import React, { useEffect } from "react";
import { Typography, Box, List, ListItem, ListItemText, Link } from "@mui/material";
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';

export default function TermsOfService() {
  useEffect(() => {
    document.title = "GenieGig | Terms of Service";
    logAnalyticsEvent(GGAnalyticsEvents.TERMS_OF_SERVICE_PAGE_LOADED);
  }, []);

  const sections = [
    "1. Definitions",
    "2. User Accounts",
    "3. Services",
    "4. Escrow Payment System",
    "5. User Responsibilities",
    "6. Prohibited Activities",
    "7. Automatic Approvals",
    "8. Dispute Resolution",
    "9. Refunds and Cancellations",
    "10. Disclaimer",
    "11. Limitation of Liability",
    "12. Termination",
    "13. Governing Law",
    "14. Entire Agreement",
    "15. Updates to Terms of Service",
    "16. Contact Us"
  ];

  return (
    <Box maxWidth="md" mx="auto" p={4} color="text.primary">
      <div style={{ display: "flex", alignItems:'center', marginBottom: "20px" }}>
        <Typography variant="h4" gutterBottom fontFamily={"Goodfish"} style={{ fontWeight: 700, marginRight: "10px" }}>
          © GenieGig
        </Typography>
        <Typography variant="h4" gutterBottom>
          Terms of Service
        </Typography>
      </div>
      
      <Box mb={3}>
        <Typography variant="body1" color="text.secondary" style={{ fontStyle: 'italic' }}>
          Last Updated: 23 October 2025
        </Typography>
      </Box>
      
      <Box mb={4}>
        <Typography variant="h6" gutterBottom>
          Contents
        </Typography>
        <List>
          {sections.map((title, index) => (
            <ListItem key={index} disablePadding>
              <Link href={`#section-${index + 1}`} underline="hover" color="blue">
                <ListItemText primary={title} />
              </Link>
            </ListItem>
          ))}
        </List>
      </Box>

      <Typography id="section-1" variant="h5" gutterBottom mt={4}>
        1. Definitions
      </Typography>
      <List>
        <ListItem><ListItemText primary="User: Any person who either hires freelancers on the platform or offers services as a freelancer." /></ListItem>
        <ListItem><ListItemText primary="Client: A user who hires Freelancers. If not registered as a Freelancer, the user is considered a Client by default." /></ListItem>
        <ListItem><ListItemText primary="Freelancer: A registered freelancer who posts Gigs on the platform to offer services." /></ListItem>
        <ListItem><ListItemText primary="Gig: A specific service posted by a freelancer that is offered to a client." /></ListItem>
        <ListItem><ListItemText primary="Escrow Payment System: A secure system that holds funds until project completion and approval." /></ListItem>
        <ListItem><ListItemText primary="Dispute: A disagreement between Client and Freelancer once order is placed by client." /></ListItem>
        <ListItem><ListItemText primary="Project: Service(s) a Freelancer agrees to provide to a Client." /></ListItem>
      </List>

      <Typography id="section-2" variant="h5" gutterBottom mt={4}>
        2. User Accounts
      </Typography>
      <List>
        <ListItem><ListItemText primary="You must be at least 18 and legally capable of entering a contract." /></ListItem>
        <ListItem><ListItemText primary="Maintain confidentiality of your account information." /></ListItem>
        <ListItem><ListItemText primary="You're responsible for all activity under your account." /></ListItem>
        <ListItem><ListItemText primary="Messages with Freelancers may be reviewed by team GenieGig at PlatinumSpark Technologies Pvt. Ltd. in cases of disputes or complaints." /></ListItem>
      </List>

      <Typography id="section-3" variant="h5" gutterBottom mt={4}>
        3. Services
      </Typography>
      <Typography paragraph>
        GenieGig connects Clients with Freelancers. Freelancers are independent contractors. Project scope, pricing, and deliverables are agreed upon by the parties.
      </Typography>

      <Typography id="section-4" variant="h5" gutterBottom mt={4}>
        4. Escrow Payment System
      </Typography>
      <Typography paragraph>
        Payments are held by GenieGig and released to the freelancer upon Client approval. Funds are not directly deposited into the Freelancer's account initially.
      </Typography>

      <Typography id="section-5" variant="h5" gutterBottom mt={4}>
        5. User Responsibilities
      </Typography>
      <Typography variant="h6" gutterBottom mt={2}>
        Clients:
      </Typography>
      <List>
        <ListItem><ListItemText primary="Provide accurate Project requirements." /></ListItem>
        <ListItem><ListItemText primary="Pay via official platform channels." /></ListItem>
        <ListItem><ListItemText primary="Communicate via official platform channels - Messaging, Call and Video Meet" /></ListItem>
        <ListItem><ListItemText primary="Review work and provide feedback thoroughly." /></ListItem>
      </List>

      <Typography variant="h6" gutterBottom mt={2}>
        Freelancers:
      </Typography>
      <List>
        <ListItem><ListItemText primary="Deliver quality work as per the requirements in agreement with the client during discussion before order placement." /></ListItem>
        <ListItem><ListItemText primary="Ensure skills and experience match Project needs." /></ListItem>
        <ListItem><ListItemText primary="Communicate effectively and keep Clients updated on the regular intervals." /></ListItem>
      </List>

      <Typography id="section-6" variant="h5" gutterBottom mt={4}>
        6. Prohibited Activities
      </Typography>
      <List>
        <ListItem><ListItemText primary="No illegal or unauthorized use." /></ListItem>
        <ListItem><ListItemText primary="Do not violate laws or regulations." /></ListItem>
        <ListItem><ListItemText primary="Do not infringe intellectual property rights." /></ListItem>
        <ListItem><ListItemText primary="No transmission of malicious code." /></ListItem>
        <ListItem><ListItemText primary="Do not create fake or impersonated accounts." /></ListItem>
      </List>

      <Typography id="section-7" variant="h5" gutterBottom mt={4}>
        7. Automatic Approvals
      </Typography>
      <Typography paragraph>
        Upon delivery of the final order by the freelancer, the client will receive an email notification. The system will continue to send reminder emails at regular intervals throughout the 48-hour period until the client takes action. If the client fails to take any action regarding the delivered order (either to approve or request revisions) within 48 hours of receiving the delivery, the system will automatically mark the order as approved and release the payment to the freelancer.
      </Typography>

      <Typography id="section-8" variant="h5" gutterBottom mt={4}>
        8. Dispute Resolution
      </Typography>
      <Typography paragraph>
       A dispute may be raised by client or freelancer through contact us page using either the helpline or email support.
       The dispute will be resolved by the team GenieGig at PlatinumSpark Technologies Pvt. Ltd. after thorough investigation.
       GenieGig may suspend or terminate accounts that breach these Terms or are found to be in violation of the Terms of Service / Privacy Policy or cause any harm to the platform or other users.
      </Typography>

      <Typography id="section-9" variant="h5" gutterBottom mt={4}>
        9. Refunds and Cancellations
      </Typography>
      <Typography paragraph>
        Upon initiation of refund to the client after proper investigation of dispute, once approved by support team GenieGig at PlatinumSpark Technologies Pvt. Ltd., the total amount will be refunded to the client's account. The amount will be processed and settled by GenieGig within 48 hours of the refund request approval.
        The amount may take a few days to reflect in the client's account depending on the bank's processing time.
        The cancellation of a successfully placed order can only be done within 24 hours of the order placement. For the orders having an execution time of less than 24 hours, the cancellation can only be done within 2 hours of the order placement.
        The cancellation of an order can only be done through the customer support of GenieGig through the contact us page using either the helpline or email support.
      </Typography>

      <Typography id="section-10" variant="h5" gutterBottom mt={4}>
        10. Disclaimer
      </Typography>
      <Typography paragraph>
        GenieGig does not provide any direct gig or service to the client or freelancer. It is a platform that connects clients and freelancers ensuring a safe and secure state of the art experience.
      </Typography>

      <Typography id="section-11" variant="h5" gutterBottom mt={4}>
        11. Limitation of Liability
      </Typography>
      <Typography paragraph>
        GenieGig is not liable for personal damages arising to any party (client or freelancer) from use of the Services provided on the platform.
      </Typography>

      <Typography id="section-12" variant="h5" gutterBottom mt={4}>
        12. Termination
      </Typography>
      <Typography paragraph>
        Either party may terminate these Terms. Users may close their account anytime.
      </Typography>

      <Typography id="section-13" variant="h5" gutterBottom mt={4}>
        13. Governing Law
      </Typography>
      <Typography paragraph>
        These Terms are governed by laws including The Copyright Act 1957, Patents Act 1970, Trade Marks Act 1999, and related intellectual property laws.
      </Typography>

      <Typography id="section-14" variant="h5" gutterBottom mt={4}>
        14. Entire Agreement
      </Typography>
      <Typography paragraph>
        These Terms constitute the entire agreement between you and GenieGig.
      </Typography>

      <Typography id="section-15" variant="h5" gutterBottom mt={4}>
        15. Updates to Terms of Service
      </Typography>
      <Typography paragraph>
        GenieGig may update these Terms anytime by posting a new version. Check periodically for changes.
      </Typography>

      <Typography id="section-16" variant="h5" gutterBottom mt={4}>
        16. Contact Us
      </Typography>
      <Typography paragraph>
        If you have any questions, please contact us at info@geniegig.com, +91-8368857097, or through the contact us page using either the helpline or email support.
        We will respond within 24 hours of receiving your inquiry. PlatinumSpark Technologies Pvt. Ltd., E-322, Tagore Garden Extension, New Delhi, Delhi 110027, India.
      </Typography>

      <Box mt={6} pt={4} borderTop={1} borderColor="grey.300">
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} GenieGig - A Product of PlatinumSpark Technologies Pvt. Ltd. All rights reserved.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Registered Office: E-322, Tagore Garden Extension, New Delhi, Delhi 110027, India
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Email: info@geniegig.com | CIN: U62099DL2024PTC440048
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contact: +91-8368857097
        </Typography>
      </Box>
    </Box>
  );
}
